import { GetCurrentWebUserResponseDTO, PermissionRoles, WorkspaceDTO } from '@gearbelt/global';
import { NavigateFunction } from 'react-router-dom';

import { ApiService } from '@/services/api-v2';
import { useAuthDenialStore } from '@/stores/auth-denial.store';
import { useSignUpReferralStore } from '@/stores/signup-referral.store';
import { useUserStore } from '@/stores/user.store';
import { getCrossAccountTargetAccountId } from '@/utils/cross-account-setup';

type WorkspaceActions = {
  setOrg: (workspace: WorkspaceDTO | null) => void;
  navigate: NavigateFunction;
};

function isSuperUser(user: GetCurrentWebUserResponseDTO): boolean {
  return user.roles.includes(PermissionRoles.SitemateSuperUser);
}

export function getRouteWorkspaceId(pathname: string): string | null {
  return pathname.match(/^\/workspaces\/([^/]+)/)?.[1] ?? null;
}

function isCrossAccountSetup(workspace: WorkspaceDTO): boolean {
  return (
    getCrossAccountTargetAccountId({
      pathname: window.location.pathname,
      search: window.location.search,
      storedReferenceSmsAccountId: useSignUpReferralStore.getState().referenceSmsAccountId,
      currentOrgAccountId: workspace.accountId,
    }) !== null
  );
}

async function jumpToWorkspaceAsSuperUser(
  user: GetCurrentWebUserResponseDTO,
  routeWorkspaceId: string | null,
  setOrg: WorkspaceActions['setOrg'],
): Promise<void> {
  try {
    const workspaceIdOverride = window.localStorage.getItem('USE_WORKSPACE_ID');
    const workspaceId = workspaceIdOverride || routeWorkspaceId || useUserStore.getState().org?.workspaceId || user.workspaces[0]?.workspaceId;
    const { workspace } = await ApiService.GetWorkspaceById({ workspaceId }).then((res) => res.json());
    setOrg(workspace);
  } catch (error) {
    console.error('Failed to fetch workspace:', error);
    window.location.href = '/';
  }
}

// Picks the workspace the user lands on after auth. Reads route + cached store state at call time (not from
// render closures) so it always sees current values; runs once when the current user resolves.
export async function resolveWorkspaceDestination(user: GetCurrentWebUserResponseDTO, { setOrg, navigate }: WorkspaceActions): Promise<void> {
  const { pathname } = window.location;

  // Wins over every branch below: a super user never receives this denial.
  if (useAuthDenialStore.getState().hasNoGearbeltRoleAnywhere) {
    if (pathname !== '/no-workspace-available') {
      navigate('/no-workspace-available');
    }
    return;
  }

  const routeWorkspaceId = getRouteWorkspaceId(pathname);

  // On a workspace route, prefer the workspace named in the URL — or let a superuser jump to it.
  if (pathname.startsWith('/workspaces')) {
    const membership = user.workspaces.find((w) => w.workspaceId === routeWorkspaceId);
    if (membership) {
      setOrg(membership);
      return;
    }
    if (isSuperUser(user)) {
      await jumpToWorkspaceAsSuperUser(user, routeWorkspaceId, setOrg);
      return;
    }
  }

  // Otherwise keep the cached workspace when it's still valid (or reusable by a superuser).
  const cachedOrg = useUserStore.getState().org;
  const belongsToCachedOrg = user.workspaces.some((w) => w.workspaceId === cachedOrg?.workspaceId);
  if (belongsToCachedOrg || (isSuperUser(user) && cachedOrg)) {
    return;
  }

  // No usable workspace yet: land on the first one, or route to onboarding when there are none.
  const [firstWorkspace] = user.workspaces;
  if (!firstWorkspace) {
    // Drop the rehydrated workspace: /no-workspace-available bounces to /dashboard while one is set.
    setOrg(null);
    navigate(useUserStore.getState().isNewUser ? '/workspace-setup' : '/no-workspace-available');
    return;
  }

  if (!isCrossAccountSetup(firstWorkspace)) {
    setOrg(firstWorkspace);
    window.location.href = '/';
  }
}
